import * as React from "react";
import CarSchematic from "../../schadenmeldung/illustrations/CarSchematic";
import { PolizeiBlock } from "./shared";
import { BIG_COPY, YES_NO, YES_NO_UNSURE, YES_NO_UNKNOWN } from "../copy";
import {
  type BigStepProps,
  type KfzFremd,
  toggleValue,
  patchAt,
  makeInjuredPerson,
  makeKfzFremd,
} from "../types";
import {
  StepHead,
  SubHead,
  Block,
  FieldGrid,
  TextField,
  TextAreaField,
  NumberField,
  RadioGroup,
  CheckboxList,
  ContactBlock,
  RepeatCard,
  AddButton,
} from "../fields";

export function KfzSchaeden({ answers, update }: BigStepProps) {
  const C = BIG_COPY.kfzSchaeden;
  return (
    <div>
      <StepHead eyebrow={C.eyebrow} title={C.title} hint={C.hint} />
      <Block>
        <CheckboxList
          options={C.options}
          values={answers.kfzSchaeden}
          onToggle={(v) => update({ kfzSchaeden: toggleValue(answers.kfzSchaeden, v) })}
        />
      </Block>
    </div>
  );
}

export function KfzWhere({ answers, update }: BigStepProps) {
  const C = BIG_COPY.kfzWhere;
  return (
    <div>
      <StepHead eyebrow={C.eyebrow} title={C.title} hint={C.hint} />
      <Block>
        <TextAreaField label={C.ortLabel} value={answers.kfzOrt} onChange={(v) => update({ kfzOrt: v })} placeholder={C.ortPlaceholder} />
      </Block>
    </div>
  );
}

export function KfzArt({ answers, update }: BigStepProps) {
  const C = BIG_COPY.kfzArt;
  return (
    <div>
      <StepHead eyebrow={C.eyebrow} title={C.title} hint={C.hint} />
      <Block>
        <RadioGroup options={C.options} value={answers.kfzArt} onChange={(v) => update({ kfzArt: v, kfzUnfallTyp: null, kfzOhneFremdTyp: null, kfzEinschaetzung: v === "unbekannt" ? "unbekannt" : answers.kfzEinschaetzung })} columns />
        {answers.kfzArt === "unfall_fremd" && (
          <RadioGroup label={C.unfallLabel} options={C.unfallOptions} value={answers.kfzUnfallTyp} onChange={(v) => update({ kfzUnfallTyp: v })} columns />
        )}
        {answers.kfzArt === "ohne_fremd" && (
          <RadioGroup label={C.ohneFremdLabel} options={C.ohneFremdOptions} value={answers.kfzOhneFremdTyp} onChange={(v) => update({ kfzOhneFremdTyp: v })} />
        )}
      </Block>
    </div>
  );
}

export function KfzEigenes({ answers, update }: BigStepProps) {
  const C = BIG_COPY.kfzEigenes;
  return (
    <div>
      <StepHead eyebrow={C.eyebrow} title={C.title} hint={C.hint} />
      <Block>
        <FieldGrid>
          <TextField label={C.kennzeichen} value={answers.kfzEigenKennzeichen} onChange={(v) => update({ kfzEigenKennzeichen: v })} />
          <TextField label={C.art} value={answers.kfzEigenArt} onChange={(v) => update({ kfzEigenArt: v })} />
          <NumberField label={C.km} value={answers.kfzEigenKm} onChange={(v) => update({ kfzEigenKm: v })} suffix="km" optional />
        </FieldGrid>
        <RadioGroup label={C.anhaenger} options={YES_NO} value={answers.kfzEigenAnhaenger} onChange={(v) => update({ kfzEigenAnhaenger: v })} columns />
        {answers.kfzEigenAnhaenger === "ja" && (
          <TextField label={C.anhaengerKz} value={answers.kfzEigenAnhaengerKz} onChange={(v) => update({ kfzEigenAnhaengerKz: v })} />
        )}
      </Block>

      <SubHead title={C.fahrerTitle} />
      <ContactBlock value={answers.kfzEigenFahrer} onChange={(patch) => update({ kfzEigenFahrer: { ...answers.kfzEigenFahrer, ...patch } })} show={{ emailOptional: true }} />

      <SubHead title={C.schaedenTitle} />
      <Block>
        <CarSchematic selected={answers.kfzEigenZonen} onToggle={(id) => update({ kfzEigenZonen: toggleValue(answers.kfzEigenZonen, id) })} />
        <RadioGroup label={C.abgeschleppt} options={YES_NO} value={answers.kfzEigenAbgeschleppt} onChange={(v) => update({ kfzEigenAbgeschleppt: v })} columns />
        <NumberField label={C.hoehe} value={answers.kfzEigenHoehe} onChange={(v) => update({ kfzEigenHoehe: v })} suffix="EUR" optional />
        <FieldGrid>
          <RadioGroup label={C.werkstatt} options={YES_NO} value={answers.kfzEigenWerkstatt} onChange={(v) => update({ kfzEigenWerkstatt: v })} columns />
          <RadioGroup label={C.gutachter} options={YES_NO} value={answers.kfzEigenGutachter} onChange={(v) => update({ kfzEigenGutachter: v })} columns />
        </FieldGrid>
      </Block>
    </div>
  );
}

export function KfzFremdes({ answers, update }: BigStepProps) {
  const C = BIG_COPY.kfzFremdes;
  const list = answers.kfzFremdFahrzeuge;
  const setList = (next: KfzFremd[]) => update({ kfzFremdFahrzeuge: next });
  const set = (i: number, patch: Partial<KfzFremd>) => setList(patchAt(list, i, patch));
  return (
    <div>
      <StepHead eyebrow={C.eyebrow} title={C.title} hint={C.hint} />
      {list.map((f, i) => (
        <RepeatCard
          key={i}
          title={list.length > 1 ? `Fremdes Fahrzeug ${i + 1}` : undefined}
          onRemove={list.length > 1 ? () => setList(list.filter((_, j) => j !== i)) : undefined}
        >
          <Block>
            <FieldGrid>
              <TextField label={C.kennzeichen} value={f.kennzeichen} onChange={(v) => set(i, { kennzeichen: v })} optional />
              <TextField label={C.art} value={f.art} onChange={(v) => set(i, { art: v })} optional />
              <TextField label={C.hersteller} value={f.hersteller} onChange={(v) => set(i, { hersteller: v })} optional />
              <TextField label={C.farbe} value={f.farbe} onChange={(v) => set(i, { farbe: v })} optional />
            </FieldGrid>
            <FieldGrid>
              <RadioGroup label={C.anhaenger} options={YES_NO} value={f.anhaenger} onChange={(v) => set(i, { anhaenger: v })} columns />
              <RadioGroup label={C.fahrbereit} options={YES_NO} value={f.fahrbereit} onChange={(v) => set(i, { fahrbereit: v })} columns />
            </FieldGrid>
          </Block>

          <SubHead title={C.fahrerTitle} />
          <ContactBlock value={f.fahrer} onChange={(patch) => set(i, { fahrer: { ...f.fahrer, ...patch } })} show={{ address: true, emailOptional: true }} />

          <SubHead title={C.halterTitle} />
          <ContactBlock value={f.halter} onChange={(patch) => set(i, { halter: { ...f.halter, ...patch } })} show={{ address: true, emailOptional: true }} />

          <SubHead title="Versicherer des fremden Fahrzeugs" />
          <Block>
            <RadioGroup label={C.versichererBekannt} options={YES_NO_UNKNOWN} value={f.versichererBekannt} onChange={(v) => set(i, { versichererBekannt: v })} columns />
            {f.versichererBekannt === "ja" && (
              <FieldGrid>
                <TextField label={C.versicherer} value={f.versicherer} onChange={(v) => set(i, { versicherer: v })} optional />
                <TextField label={C.vsnr} value={f.vsnr} onChange={(v) => set(i, { vsnr: v })} optional />
                <TextField label={C.schadennr} value={f.schadennr} onChange={(v) => set(i, { schadennr: v })} optional />
                <TextField label={C.ansprech} value={f.ansprech} onChange={(v) => set(i, { ansprech: v })} optional />
              </FieldGrid>
            )}
            <RadioGroup label={C.vorsteuer} options={YES_NO_UNKNOWN} value={f.vorsteuer} onChange={(v) => set(i, { vorsteuer: v })} columns />
          </Block>

          <SubHead title={C.schaedenTitle} />
          <Block>
            <CarSchematic selected={f.zonen} onToggle={(id) => set(i, { zonen: toggleValue(f.zonen, id) })} />
            <RadioGroup label={C.abgeschleppt} options={YES_NO} value={f.abgeschleppt} onChange={(v) => set(i, { abgeschleppt: v })} columns />
            <NumberField label={C.hoehe} value={f.hoehe} onChange={(v) => set(i, { hoehe: v })} suffix="EUR" optional />
            <RadioGroup label={C.kv} options={YES_NO} value={f.kv} onChange={(v) => set(i, { kv: v })} columns />
            {f.kv === "ja" && (
              <p style={{ margin: 0, color: "#6c6171", fontSize: "0.86rem" }}>{C.kvHint}</p>
            )}
            <RadioGroup label={C.mietwagen} options={YES_NO_UNSURE} value={f.mietwagen} onChange={(v) => set(i, { mietwagen: v })} columns />
            {f.mietwagen === "ja" && (
              <TextAreaField label={C.mietwagenDetail} value={f.mietwagenDetail} onChange={(v) => set(i, { mietwagenDetail: v })} />
            )}
          </Block>
        </RepeatCard>
      ))}
      <AddButton label={C.weiteresFahrzeug} onClick={() => setList([...list, makeKfzFremd()])} />
    </div>
  );
}

export function KfzHergang({ answers, update }: BigStepProps) {
  const C = BIG_COPY.kfzHergang;
  return (
    <div>
      <StepHead eyebrow={C.eyebrow} title={C.title} hint={C.hint} />
      <Block>
        <RadioGroup options={C.einschaetzungOptions} value={answers.kfzEinschaetzung} onChange={(v) => update({ kfzEinschaetzung: v })} />
      </Block>

      <SubHead title={C.zeugenTitle} />
      <Block>
        <RadioGroup options={YES_NO_UNKNOWN} value={answers.kfzZeugen} onChange={(v) => update({ kfzZeugen: v })} columns />
        {answers.kfzZeugen === "ja" && (
          <>
            <ContactBlock value={answers.kfzZeugenContact} onChange={(patch) => update({ kfzZeugenContact: { ...answers.kfzZeugenContact, ...patch } })} show={{ address: true, emailOptional: true }} />
            <TextAreaField label={C.zeugenAussage} value={answers.kfzZeugenAussage} onChange={(v) => update({ kfzZeugenAussage: v })} />
          </>
        )}
      </Block>

      <SubHead title={C.skizzeTitle} />
      <Block>
        <CheckboxList options={C.skizzeOptions} values={answers.kfzSkizze} onToggle={(v) => update({ kfzSkizze: toggleValue(answers.kfzSkizze, v) })} columns />
      </Block>

      {(answers.kfzSchaeden.includes("eigenes") ||
        answers.kfzSchaeden.includes("fremdes") ||
        answers.kfzSchaeden.includes("person")) && (
        <PolizeiBlock
          polizei={answers.kfzPolizei}
          aktenzeichen={answers.kfzPolizeiAktenzeichen}
          dienststelle={answers.kfzPolizeiDienststelle}
          onChange={(p) =>
            update({
              ...(p.polizei !== undefined ? { kfzPolizei: p.polizei } : {}),
              ...(p.aktenzeichen !== undefined ? { kfzPolizeiAktenzeichen: p.aktenzeichen } : {}),
              ...(p.dienststelle !== undefined ? { kfzPolizeiDienststelle: p.dienststelle } : {}),
            })
          }
        />
      )}
    </div>
  );
}

export function KfzPerson({ answers, update }: BigStepProps) {
  const C = BIG_COPY.kfzPerson;
  const persons = answers.kfzPersonen;
  const setPersons = (next: typeof persons) => update({ kfzPersonen: next });
  const patch = (i: number, p: Partial<(typeof persons)[number]>) => setPersons(patchAt(persons, i, p));
  return (
    <div>
      <StepHead eyebrow={C.eyebrow} title={C.title} hint={C.hint} />
      {persons.map((p, i) => (
        <RepeatCard
          key={i}
          title={persons.length > 1 ? `Verletzte Person ${i + 1}` : undefined}
          onRemove={persons.length > 1 ? () => setPersons(persons.filter((_, j) => j !== i)) : undefined}
        >
          <Block>
            <TextField label={C.name} value={p.name} onChange={(v) => patch(i, { name: v })} />
            <RadioGroup label={C.rolleLabel} options={C.rolleOptions} value={p.rolle} onChange={(v) => patch(i, { rolle: v })} columns />
            {p.rolle === "sonstiges" && (
              <TextField label={C.rolleDetail} value={p.rolleDetail} onChange={(v) => patch(i, { rolleDetail: v })} />
            )}
            <RadioGroup label={C.zugehoerigLabel} options={C.zugehoerigOptions} value={p.zugehoerig} onChange={(v) => patch(i, { zugehoerig: v })} />
            <TextAreaField label={C.verletzung} value={p.verletzung} onChange={(v) => patch(i, { verletzung: v })} />
            <FieldGrid>
              <RadioGroup label={C.arbeitsunfaehig} options={YES_NO_UNKNOWN} value={p.arbeitsunfaehig} onChange={(v) => patch(i, { arbeitsunfaehig: v })} columns />
              <RadioGroup label={C.unterlagen} options={YES_NO} value={p.unterlagen} onChange={(v) => patch(i, { unterlagen: v })} columns />
            </FieldGrid>
          </Block>
        </RepeatCard>
      ))}
      <AddButton label={C.weitereLabel} onClick={() => setPersons([...persons, makeInjuredPerson()])} />
    </div>
  );
}

export function KfzSachen({ answers, update }: BigStepProps) {
  const C = BIG_COPY.kfzSachen;
  return (
    <div>
      <StepHead eyebrow={C.eyebrow} title={C.title} hint={C.hint} />
      <Block>
        <TextAreaField label={C.beschreibung} value={answers.kfzSachenBeschreibung} onChange={(v) => update({ kfzSachenBeschreibung: v })} />
        <RadioGroup label={C.eigentuemerLabel} options={C.eigentuemerOptions} value={answers.kfzSachenEigentuemer} onChange={(v) => update({ kfzSachenEigentuemer: v })} columns />
        {answers.kfzSachenEigentuemer === "fremd" && (
          <TextField label={C.fremdAngaben} value={answers.kfzSachenFremdAngaben} onChange={(v) => update({ kfzSachenFremdAngaben: v })} />
        )}
        <TextAreaField label={C.hergang} value={answers.kfzSachenHergang} onChange={(v) => update({ kfzSachenHergang: v })} />
        <NumberField label={C.hoehe} value={answers.kfzSachenHoehe} onChange={(v) => update({ kfzSachenHoehe: v })} suffix="EUR" optional />
        <CheckboxList label={C.belegeLabel} options={C.belegeOptions} values={answers.kfzSachenBelege} onToggle={(v) => update({ kfzSachenBelege: toggleValue(answers.kfzSachenBelege, v) })} columns />
        <RadioGroup label={C.vorsteuer} options={YES_NO_UNKNOWN} value={answers.kfzSachenVorsteuer} onChange={(v) => update({ kfzSachenVorsteuer: v })} columns />
      </Block>
    </div>
  );
}
